<template>
  <span class="dayTags" :style="{'--pillScale': pillScale}">
    <!-- the pills in and out sideways, as an event opens into its days and back (Seán,
         2026-10-29), in step with the days below (Table.vue) -->
    <TransitionGroup
      ref="groupRef"
      tag="span"
      class="pillRow"
      :class="{isMoving}"
      :css="false"
      @enter="pillHooks.onEnter"
      @leave="pillHooks.onLeave">
      <template v-for="item in pillItems" :key="item.key">
        <span v-if="item.isBreak" class="rowBreak" aria-hidden="true" />
        <!-- back to every event, right after the days shown (Seán, 2026-10-29) -->
        <Tag
          v-else-if="item.days === SHOW_ALL"
          class="dayTagMinWidth showAll"
          :label="SHOW_ALL_LABEL"
          selected=""
          hide-cross-symbol
          @update="onShowAllUpdate" />
        <Tag
          v-else
          :class="getDayTagClass(item.pillId)"
          :label="getPillLabel(item.days)"
          :selected="item.days.length === 1 ? getPillSelected(item.days[0]) : ''"
          hide-cross-symbol
          @update="(value, clickEvent) => onPillUpdate(item.days, value, clickEvent)" />
      </template>
    </TransitionGroup>
    <!-- every event's pill, unseen, at full size: the text is fitted to these, so its size stays
         put when an event opens into its days (Seán, 2026-10-29) -->
    <span ref="measureRef" class="measureRow" inert>
      <Tag
        v-for="(days, eventId) in events"
        :key="days.join()"
        :class="getMeasureClass(eventId)"
        :label="getPillLabel(days)"
        selected=""
        hide-cross-symbol />
    </span>
  </span>
</template>

<script setup>
import {computed, nextTick, ref, shallowRef, watch} from "vue";
import {useRoute} from "vue-router";
import {useResizeObserver} from "@vueuse/core";
import Tag from "@/components/Tag";
import {getIsEventName, getProgramEvents} from "@/composables/programEvents";
import {getPillHooks} from "@/composables/pillTransitions";

const log = (msg) => process.env.VUE_APP_DEV && console.log(); // eslint-disable-line
const props = defineProps(["programDays", "program", "daySelected"]); // eslint-disable-line
const emit = defineEmits(["update", "toggleDay"]);

// on macOS, ctrl+click opens the context menu, so cmd (meta) is also accepted
const hasModifier = (clickEvent) => {
  if (!clickEvent) return false;
  return clickEvent.shiftKey || clickEvent.ctrlKey || clickEvent.metaKey;
};

const isDayTag = (value) => /^[A-Z][a-z]{2}\s\d{1,2}$/.test(value || "");

// the pill's label from the day's date: "Tue, April 14, 2026" -> "Tue\nApr 14" (the key, "Apr 14",
// has no weekday)
function getDayTagLabel(dayKey) {
  const [weekday, month, day] = (props.program?.[dayKey]?.date || "").split(" ");
  if (!month || !day) return dayKey;
  return `${weekday.replace(",", "")}\n${month.slice(0, 3)} ${day.replace(",", "")}`;
}

// the pills, filters (Seán, 2026-10-29): one per event (its day, or its range of days), a
// selected one in the brand colour; with a day of a multi-day event selected, only that event,
// one pill per day (so each can be chosen alone), then Show All Dates to go back
const getIsSelected = (day) => Boolean(props.daySelected?.includes(day));
const events = computed(() => getProgramEvents(props.program, props.programDays || []));
const openEvents = computed(() => {
  const selected = events.value.filter((days) => days.some(getIsSelected));
  return selected.some((days) => days.length > 1) ? selected : [];
});
// on two lines, as the date pills, so as big as they are (Seán, 2026-10-29)
const SHOW_ALL_LABEL = "Show All\nDates";
// Show All Dates' place in the row's list, after the days shown
const SHOW_ALL = ["showAll"];
const pills = computed(() => {
  if (!openEvents.value.length) return events.value;
  return [...openEvents.value.flatMap((days) => days.map((day) => [day])), SHOW_ALL];
});

// the pills shown, and Show All Dates, changing all at once (Seán, 2026-10-29): every pill
// coming or going grows or shrinks on one curve over the same 450ms (pillTransitions.js), so the
// row's width moves one way, from where it was to where it ends, never back. Opening a multi-day
// event, its days grow in and Show All Dates with them as the other events shrink away; closing,
// the reverse. The row doesn't wrap while they move
const TRANSITION_MS = 450;
const pillHooks = getPillHooks(TRANSITION_MS);
const shownPills = shallowRef([]); // its items as they are: Show All Dates is told by identity
const isMoving = ref(false);
let movingTimer = null;
function markMoving() {
  clearTimeout(movingTimer);
  isMoving.value = true;
  movingTimer = setTimeout(() => (isMoving.value = false), TRANSITION_MS + 50);
}
// the old pills and the new together, in date order (the old first), so each new pill grows in
// where it ends: the Workshop's days where its pill was, not after the others; Show All Dates
// just after the day before it, so the events after it grow in where it was
function getRanked(list) {
  const order = props.programDays || [];
  const getRank = (days, id) => {
    if (days !== SHOW_ALL) return order.indexOf(days[0]);
    return order.indexOf(list[id - 1]?.at(-1)) + 0.5;
  };
  return list.map((days, id) => ({days, rank: getRank(days, id)}));
}
function getMerged(previous, next) {
  const keys = new Set(previous.map((days) => days.join()));
  const added = getRanked(next).filter(({days}) => !keys.has(days.join()));
  const merged = [...getRanked(previous), ...added].sort((one, other) => one.rank - other.rank);
  return merged.map(({days}) => days);
}
// for one tick, old and new together (the new start growing in place), then only the new (the
// old start shrinking): both in the same frame
watch(pills, async (next, previous) => {
  if (!previous) return (shownPills.value = next);
  markMoving();
  shownPills.value = getMerged(previous, next);
  await nextTick();
  shownPills.value = next;
}, {immediate: true});

// a folded range's label: "Tue–Thu\nApr 14–23", or "Thu–Sat\nApr 30–May 2"
// a day without its date yet (a time zone change updates the days and their dates a moment
// apart) keeps its key, as getDayTagLabel does (production, 2026-11-01: a TypeError on Tokyo)
function getRangeLabel(days) {
  const [firstWeekday, firstDate] = getDayTagLabel(days[0]).split("\n");
  const [lastWeekday, lastDate] = getDayTagLabel(days[days.length - 1]).split("\n");
  if (!firstDate || !lastDate) return `${firstWeekday}–${lastWeekday}`;
  const [firstMonth] = firstDate.split(" ");
  const lastPart = lastDate.startsWith(firstMonth) ? lastDate.split(" ")[1] : lastDate;
  return `${firstWeekday}–${lastWeekday}\n${firstDate}–${lastPart}`;
}

const getPillLabel = (days) => (days.length === 1 ? getDayTagLabel(days[0]) : getRangeLabel(days));

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_MS = 24 * 60 * 60 * 1000;

// "Tue, April 14, 2026" -> its UTC midnight in ms (null if unreadable)
function getDayTime(dayKey) {
  const date = props.program?.[dayKey]?.date || "";
  const match = date.match(/([A-Za-z]{3})[a-z]*\s+(\d{1,2}),?\s+(\d{4})/);
  if (!match) return null;
  const month = MONTHS.indexOf(match[1]);
  if (month === -1) return null;
  return Date.UTC(Number(match[3]), month, Number(match[2]));
}

// a gap before a pill unless its first day directly follows the previous pill's last (e.g.
// Nov 3 | Nov 18, Nov 19)
function getIsGapBetween(previousDays, days) {
  if (!previousDays) return false;
  const previous = getDayTime(previousDays.at(-1));
  const current = getDayTime(days[0]);
  if (previous === null || current === null) return false;
  return current - previous !== DAY_MS;
}
// the pills, each after its line break when it starts a row: one keyed list, for TransitionGroup
const pillItems = computed(() =>
  shownPills.value.flatMap((days, pillId) => {
    const pill = {key: days.join(), days, pillId};
    if (!rowStartIds.value.includes(pillId)) return [pill];
    return [{key: `break-${pill.key}`, isBreak: true}, pill];
  })
);
const getMeasureClass = (eventId) => {
  const isGap = getIsGapBetween(events.value[eventId - 1], events.value[eventId]);
  return isGap ? "dayTagMinWidth gapLeft" : "dayTagMinWidth";
};
// the gap from the last pill with a date before it (Show All Dates has none: looked past)
const getIsGapFromPrevious = (pillId) => {
  const before = shownPills.value.slice(0, pillId).filter((days) => days !== SHOW_ALL);
  return getIsGapBetween(before.at(-1), shownPills.value[pillId]);
};

// A gapped pill that wraps to the start of a row loses its gap there, however many
// rows there are: measured with every gap on; then a line break goes before it (.rowBreak), so it
// stays at the row's start without its gap rather than moving back up (2026-10-13)
const groupRef = ref(null);
const rowRef = computed(() => groupRef.value?.$el || null);
const rowStartIds = ref([]);
let lastWidth = 0;
function getPills() {
  return [...(rowRef.value?.querySelectorAll(":scope > :not(.rowBreak)") || [])];
}
function getRowStartIds() {
  const pills = getPills();
  const isRowStart = (pill, id) => id > 0 && pill.offsetTop > pills[id - 1].offsetTop + 2;
  return pills.map((pill, id) => (isRowStart(pill, id) ? id : -1)).filter((id) => id > 0);
}
// the rows as laid out now match the breaks: every break's pill starts a row, and no pill still
// carrying its gap starts one
function getIsSettled() {
  const pills = getPills();
  const starts = getRowStartIds();
  const isGapped = (id) => pills[id]?.classList.contains("gapLeft");
  return rowStartIds.value.every((id) => starts.includes(id)) && !starts.some(isGapped);
}

// measured with every gap on; again (a few times at most) if the layout moved meanwhile, e.g. a
// font or the selected day's bold label arriving
// on a phone, the pills' text as big as fits them on one row, up to its size on wider screens
// (Seán, 2026-10-29): measured at full size, every gap on, then scaled down to the row's width
const pillScale = ref(1);
const measureRef = ref(null);
// what doesn't scale with the text (gaps, margins, borders, a pixel for rounding), and what does
function getPillNeeds(pill) {
  const style = getComputedStyle(pill);
  const border = parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
  const width = pill.getBoundingClientRect().width;
  return {fixed: parseFloat(style.marginLeft) + border, scaled: width - border};
}
function getRowNeeds() {
  const pills = [...(measureRef.value?.children || [])].map(getPillNeeds);
  const gap = parseFloat(getComputedStyle(measureRef.value).columnGap) || 0;
  const fixed = pills.reduce((sum, pill) => sum + pill.fixed, gap * (pills.length - 1) + 1);
  const widths = pills.reduce((sum, pill) => sum + pill.scaled, 0);
  return {fixed, widths};
}
async function fitPills() {
  const isSmall = ["narrow", "medium"].some((size) => document.body.classList.contains(size));
  if (!rowRef.value || !measureRef.value || !isSmall) return (pillScale.value = 1);
  await nextTick();
  const {fixed, widths} = getRowNeeds();
  const available = rowRef.value.clientWidth;
  const scale = Math.floor(((available - fixed) / widths) * 100) / 100;
  pillScale.value = widths ? Math.min(1, scale) : 1;
}

async function markRowStarts(attempt = 0) {
  rowStartIds.value = [];
  await nextTick();
  if (attempt === 0) await fitPills();
  await nextTick(); // the fitted size laid out before the rows are measured
  rowStartIds.value = getRowStartIds();
  await nextTick();
  if (attempt < 3 && !getIsSettled()) await markRowStarts(attempt + 1);
}
if (typeof document !== "undefined") document.fonts?.ready.then(() => markRowStarts());
// a new width (not the height the breaks change), or other days (e.g. another time zone)
useResizeObserver(rowRef, ([entry]) => {
  const width = Math.round(entry.contentRect.width);
  if (width === lastWidth) return;
  lastWidth = width;
  markRowStarts();
});
// once the pills have come and gone (the transition)
const markAfterTransition = () => setTimeout(markRowStarts, TRANSITION_MS + 50);
watch(() => [shownPills.value, props.daySelected?.length], markAfterTransition, {deep: true});

const getDayTagClass = (dayId) => {
  if (!getIsGapFromPrevious(dayId) || rowStartIds.value.includes(dayId)) return "dayTagMinWidth";
  return "dayTagMinWidth gapLeft";
};


function switchSelectedDay(day) {
  log(`switchSelectedDay(${day})`);
  if (day === "Show All") day = "";
  emit("update", day);
}

// a pill reads as selected when its own label matches the Tag's selected prop
function getPillSelected(dayKey) {
  if (!Array.isArray(props.daySelected)) return "";
  if (!props.daySelected.includes(dayKey)) return "";
  return getDayTagLabel(dayKey);
}

// an event's pill selects its days
function onPillUpdate(days, emittedValue, clickEvent) {
  if (days.length > 1) return emit("update", days);
  onDayTagUpdate(days[0], emittedValue, clickEvent);
}

// a selected day, clicked: that day alone, or, when it's the only one, every day again
function onDayTagUpdate(dayKey, emittedValue, clickEvent) {
  if (hasModifier(clickEvent)) return emit("toggleDay", dayKey);
  const isOnlyDay = props.daySelected?.length === 1;
  if (!emittedValue && isOnlyDay) return switchSelectedDay("");
  switchSelectedDay(dayKey);
}

function onShowAllUpdate() {
  switchSelectedDay("Show All");
}


const route = useRoute();
(function () {
  log("Immediately invoke function to set initial daySelected based on hash");
  const parameters = route.hash.replace(/^#/, "").split("&");
  const rawDays = decodeURIComponent(parameters[0] || "").split(",");
  const days = rawDays.map((token) => token.replace(/-/g, " ").replace(/\s+/g, " ").trim());
  // a day, or an event's name (#online-workshop: its days, Table.vue; programEvents.js)
  const validDays = days.filter((day) => isDayTag(day) || getIsEventName(day));
  if (route.name === "Program" && validDays.length) {
    log(`URL includes day(s) '${parameters[0]}'`);
    emit("update", validDays);
  }
})();
</script>

<style scoped>
.dayTags {
  position: relative; /* for .measureRow */
  display: block;
}
.pillRow,
.measureRow {
  --pillGap: 0.5rem;
  display: flex; /* default: flex direction = row */
  flex-wrap: wrap; /* the pills wrap on narrow screens: unwrapped, their row set the minimum page
                      width (body is inline-block), pushing the whole page wider than a phone */
  justify-content: flex-start;
  gap: var(--pillGap); /* ▶ EDIT HERE: space between pills, and between wrapped rows */
}
.dayTagLabel {
  display: block;
  line-height: 1.05;
  text-align: center;
}
/* the pills (Seán, 2026-10-29; DESIGN.md § 5): you can press these, the brand's light version as
   the time zone menu beside them; the one selected, a state, in the full brand colour. The weekday
   a step quieter (colour only: a size or weight on ::first-line throws off the pill's width) */
.dayTagMinWidth {
  min-width: 3.2rem;
  margin-top: 0;
  padding: 0.35em 0.7em;
  color: color-mix(in srgb, white var(--second-text-lift), color-mix(in srgb, black
    var(--second-text-darken), var(--brand-color)));
  background: color-mix(in srgb, var(--brand-color) var(--second-share), var(--second-base));
  border: 1px solid color-mix(in srgb, var(--brand-color) var(--second-border-share),
    var(--second-border-base));
  border-radius: 6px;
  font-weight: 500;
  flex-shrink: 0; /* its own width, never squeezed, even while pills come and go */
}
.dayTagMinWidth :deep(.tagLabel) {
  line-height: 1.2;
  white-space: pre; /* its two lines, each unbroken */
}
.dayTagMinWidth :deep(.tagLabel)::first-line {
  color: color-mix(in srgb, currentColor 70%, transparent);
}
.dayTagMinWidth:hover {
  background: color-mix(in srgb, var(--brand-color) var(--second-share), var(--second-base));
  border-color: var(--brand-color);
}
.dayTagMinWidth.selected,
.dayTagMinWidth.selected:hover {
  color: #1c1c1c;
  background: var(--brand-color);
  border-color: var(--brand-color);
  filter: none;
}
.gapLeft {
  margin-left: 1rem;
}
/* on a phone or small tablet, every event's pill on one row (Seán, 2026-10-29): tighter gaps, the
   text as big as fits (fitPills) */
.narrow .pillRow,
.medium .pillRow,
.narrow .measureRow,
.medium .measureRow {
  --pillGap: 0.3rem;
}
/* one row there (fitPills), also while pills come and go */
.narrow .pillRow,
.medium .pillRow {
  flex-wrap: nowrap;
  overflow-x: clip;
  overflow-clip-margin: 4px; /* room for the focus ring */
  contain: inline-size; /* its pills never widen the page (body is inline-block) */
}
.narrow .dayTagMinWidth,
.medium .dayTagMinWidth {
  min-width: 3.2em; /* as wider screens' 3.2rem, scaled with the text */
  font-size: calc(var(--pillScale, 1) * 1em); /* as big as fits: fitPills */
  padding-inline: 0.4em;
}
.narrow .gapLeft,
.medium .gapLeft {
  margin-left: 0.45rem;
}
.narrow .rowBreak,
.medium .rowBreak {
  margin-bottom: -0.3rem; /* the row gap above */
}
/* Show All Dates as tall as the pills, its two lines alike */
.showAll {
  align-self: stretch;
}
.showAll :deep(.tagLabel)::first-line {
  color: inherit;
}
/* a line break before a gapped day that starts a row: a full-width line of no height, its own
   row gap taken back */
/* while pills come and go (pillTransitions.js): one row */
.pillRow.isMoving {
  flex-wrap: nowrap;
}
/* unseen, unwrapped, at full size (fitPills) */
.measureRow {
  position: absolute;
  top: 0;
  left: -10000px; /* off the page, to its left: no scrolling */
  width: max-content; /* its pills unsqueezed */
  visibility: hidden;
  flex-wrap: nowrap;
  pointer-events: none;
  --pillScale: 1;
}
.rowBreak {
  flex-basis: 100%;
  height: 0;
  margin-bottom: -0.5rem; /* the row gap (span gap above) */
}
</style>
